import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import api from "../../../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import toastOptions from "../../../utils/toastOptions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { utils } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import MemberBaptizedChart from "../member-baptized-chart/MemberBaptizedChart";
import moment from "moment";
import CongregationSelect from "../../../components/congregation-select";

export default function MemberBaptizedReport() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([] as number[]);
  const [byCongregation, setByCongregation] = useState<boolean>(false);
  const initialYear = useRef<number>(2023);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [congregation, setCongregation] = useState<number>(0);

  const schema = yup.object().shape({
    year: yup.string().required("Ano é obrigatório")
  });

  const { register, handleSubmit, reset, clearErrors, errors, formState } =
    useForm({
      resolver: yupResolver(schema),
    });

  useEffect(() => {
    clearErrors();
    setByCongregation(false);
    handleYears();
  }, []);

  const handleYears = async () => {    
    setYears([currentYear]);

    try {
      let params = {
        year: currentYear
      } as any;

      const { data } = await api.get("/members/chart/baptized", {
        params: params
      });

      if (data.success) {
        initialYear.current = data.item[0]?.year;
      }

      let initYear = currentYear - 1;
  
      if (moment().month() < 1) {
        initYear = initYear - 1;
        setCurrentYear(initYear);
      }
  
      for (let i = initYear; i >= initialYear.current; i--) {
        setYears(array => [...array, i]);
      }
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.warning(m, toastOptions);
      });
    }
  }

  const onSubmit = async (request: any) => {
    try {
      const payload = {
        ...request,
        byCongregation: byCongregation
      };

      setIsLoading(true);

      const { data } = await api.post(`members/baptized/report`, payload);
    
      const nameLetter = `relatório-batismo-${new Date().getTime()}.pdf`;

      utils.printPDF(nameLetter, data.item);      
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div id="page-member-baptized-report">
      <div className="uk-card uk-card-default uk-card-small uk-card-hover uk-margin-large-bottom">
        <form onSubmit={handleSubmit<any>(onSubmit)} onReset={reset}>
          <div className="uk-card-header">
            <div className="uk-grid-small uk-flex-middle" uk-grid="true">
              <div className="uk-width-expand@m">
                <h3 className="uk-card-title uk-margin-remove-bottom">
                  Registros de Batismo Realizados
                </h3>
                <h5 className="uk-text-meta uk-text-small uk-margin-remove-top">Dados desde {initialYear.current}</h5>
              </div>
              <button
                className="uk-button uk-button-default uk-button-small"
                onClick={() => navigate('/secretaria')}
                disabled={formState.isSubmitting}
                type="button"
              >
                Voltar
              </button>
            </div>
          </div>
          <div className="uk-modal-body">
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-1-6@m uk-flex uk-flex-middle">
                <label htmlFor="year" className="uk-margin-small-right">Ano</label>
                <div className="uk-form-controls">
                  <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                    <select className="uk-form-small"
                      id="year" 
                      placeholder="Ano"
                      name="year"
                      value={currentYear}
                      ref={register}
                      onChange={(ev) => { setCurrentYear(+(ev.target.value)); setRefresh(!refresh); }}
                    >  
                      {
                        years?.map(y => {
                            return <option key={`ano${y}`} value={`${y}`}>{y}</option>
                        })
                      }
                    </select>
                    <button 
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{textTransform: 'none'}} 
                      type="button" 
                      tabIndex={-1}
                    >
                      <span></span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                </div>
                <small className="uk-text-danger">{errors.year?.message}</small>
              </div>
              <div className="uk-width-1-4@m uk-flex uk-flex-middle">
                <label className="uk-form-label uk-margin-small-right" htmlFor="byCongregation">Por Congregação?</label>
                <input className="uk-checkbox" 
                  type="checkbox"    
                  id="byCongregation"
                  name="byCongregation"
                  checked={byCongregation}
                  onChange={(e) => { setByCongregation(e.target.checked); setRefresh(!refresh); }}/>
              </div>
              {byCongregation &&
                <div className="uk-width-1-3@m uk-flex uk-flex-center">
                  <CongregationSelect afterSelect={(id: number)=> setCongregation(id)}/>
                </div>
              }
              {!byCongregation &&
                <div className="uk-width-1-4@m uk-flex uk-flex-right">
                  <button
                    disabled={(formState.isSubmitting || isLoading)}
                    className="uk-button uk-button-primary uk-button-small"
                    type="submit"
                  >
                    {(formState.isSubmitting || isLoading) && <div uk-spinner="true; ratio:0.5" className="uk-margin-small-right"></div>}
                    Imprimir
                    <FontAwesomeIcon className="uk-margin-small-left" icon={faPrint} />
                  </button>
                </div>
              }
              <div className="uk-width-1@m uk-margin-top">
                <MemberBaptizedChart 
                  refresh={refresh}
                  byCongregation={byCongregation}
                  year={currentYear}
                  idCongregation={congregation} />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}